import { forwardRef, type MouseEvent } from 'react'
import { Link, useLocation, useNavigate, useResolvedPath, type LinkProps } from 'react-router-dom'
import { isAdminShellPath, isR5ShellPath } from './route-chrome'
import { navigateWithTransition, routeMove, type RouteMove } from './view-transition'

export interface R5LinkProps extends LinkProps {
  /**
   * Animate the route change with a View Transition when the browser and the visitor allow it. `true` (default) infers the
   * move from the paths (deeper is forward, shallower is back); pass `lateral` for top-level navigation. `false` is a plain link.
   */
  transition?: boolean | RouteMove
}

/**
 * A router link that changes page through a same-document View Transition (APP-DESIGN-DIRECTION 3.9: lateral, forward or
 * back; the bars stay put). Only between two pages of the same shell (student or admin), never into the exam room. Without
 * support, or under reduced motion, it is an ordinary `Link`: same href, same keyboard behaviour, no animation.
 */
export const R5Link = forwardRef<HTMLAnchorElement, R5LinkProps>(function R5Link(
  { transition = true, onClick, to, replace, state, target, reloadDocument, download, ...rest },
  ref,
) {
  const navigate = useNavigate()
  const location = useLocation()
  const resolved = useResolvedPath(to)

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event)
    if (event.defaultPrevented || !transition) return
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    if ((target && target !== '_self') || reloadDocument || download !== undefined) return
    // Only pages inside the student shell report a route commit. A transition into the exam room, login or admin would freeze
    // the old page for the full time limit and then animate the new page's first paint, so those links stay ordinary links.
    const sameShell = isR5ShellPath(resolved.pathname)
      || (isAdminShellPath(resolved.pathname) && isAdminShellPath(location.pathname))
    if (!sameShell) return
    // Same page: let the default handling keep the scroll position and history behaviour of an ordinary link.
    if (resolved.pathname === location.pathname && resolved.search === location.search) return
    event.preventDefault()
    const move = transition === true ? routeMove(location.pathname, resolved.pathname) : transition
    navigateWithTransition(() => navigate(to, { replace, state }), move)
  }

  return <Link ref={ref} to={to} replace={replace} state={state} target={target} reloadDocument={reloadDocument} download={download} onClick={handleClick} {...rest} />
})
